import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ENV } from '../../config/env';
import { supabase } from '../../config/supabaseClient';

type AccessType = 'view' | 'edit';

type ShareRow = {
  id: string;
  business_plan_id: string;
  owner_id: string;
  access_type: AccessType;
  share_token: string;
  created_at: string;
  expires_at: string | null;
  is_active: boolean;
};

export default function BusinessPlanShareModal(props: {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
}) {
  const { isOpen, onClose, planId } = props;
  const [loading, setLoading] = useState(false);
  const [shares, setShares] = useState<ShareRow[]>([]);
  const [accessType, setAccessType] = useState<AccessType>('view');
  const [expiresInDays, setExpiresInDays] = useState<number | ''>('');
  const baseUrl = useMemo(() => window.location.origin, []);

  const fetchShares = async () => {
    setLoading(true);
    try {
      const sessionResp = await supabase.auth.getSession();
      const token = sessionResp?.data?.session?.access_token || '';
      const resp = await fetch(`${ENV.API_URL}/api/business-plans/shares?businessPlanId=${encodeURIComponent(planId)}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Failed to load share links');
      }
      const json = await resp.json();
      setShares(Array.isArray(json?.data) ? json.data : []);
    } catch (e: any) {
      console.error('Failed to fetch shares:', e);
      toast.error(e?.message || 'Failed to load share links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    void fetchShares();
  }, [isOpen, planId]);

  const copyShareUrl = async (token: string) => {
    const url = `${baseUrl}/business-plan/shared/${token}`;
    await navigator.clipboard.writeText(url);
    toast.success('Share link copied');
  };

  const revokeShare = async (shareId: string) => {
    try {
      const sessionResp = await supabase.auth.getSession();
      const token = sessionResp?.data?.session?.access_token || '';
      const resp = await fetch(`${ENV.API_URL}/api/business-plans/shares/${shareId}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Failed to revoke share');
      }
      toast.success('Share revoked');
      await fetchShares();
    } catch (e: any) {
      console.error('Failed to revoke share:', e);
      toast.error(e?.message || 'Failed to revoke share');
    }
  };

  const createShare = async () => {
    try {
      const sessionResp = await supabase.auth.getSession();
      const token = sessionResp?.data?.session?.access_token || '';

      const expiresAt =
        typeof expiresInDays === 'number'
          ? new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString()
          : undefined;

      const resp = await fetch(`${ENV.API_URL}/api/business-plans/shares`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          businessPlanId: planId,
          accessType,
          ...(expiresAt ? { expiresAt } : {}),
        }),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Failed to create share');
      }

      const json = await resp.json();
      const shareToken = json?.data?.share_token;
      if (shareToken) {
        await copyShareUrl(shareToken);
      } else {
        toast.success('Share created');
      }

      await fetchShares();
    } catch (e: any) {
      console.error('Failed to create share:', e);
      toast.error(e?.message || 'Failed to create share');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <div className="text-lg font-semibold text-gray-900">Share links</div>
            <div className="mt-1 text-xs text-gray-600">Create, copy, and revoke share links for this plan.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700">Access</label>
              <select
                value={accessType}
                onChange={(e) => setAccessType(e.target.value as AccessType)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="view">View</option>
                <option value="edit">Edit</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700">Expires</label>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value ? Number(e.target.value) : '')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Never</option>
                <option value="1">1 day</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => void createShare()}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                Create share link
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-800">
              Existing links
            </div>
            <div className="p-4">
              {loading ? <div className="text-sm text-gray-600">Loading…</div> : null}
              {!loading && shares.length === 0 ? <div className="text-sm text-gray-600">No links yet.</div> : null}
              <div className="space-y-2">
                {shares.map((s) => {
                  const url = `${baseUrl}/business-plan/shared/${s.share_token}`;
                  return (
                    <div key={s.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs text-gray-500">
                            {s.access_type.toUpperCase()} · {s.is_active ? 'Active' : 'Revoked'}
                            {s.expires_at ? ` · Expires ${new Date(s.expires_at).toLocaleDateString()}` : ''}
                          </div>
                          <div className="mt-1 truncate text-sm text-gray-800">{url}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => void copyShareUrl(s.share_token)}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                          >
                            Copy
                          </button>
                          <button
                            type="button"
                            disabled={!s.is_active}
                            onClick={() => void revokeShare(s.id)}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-gray-50 disabled:opacity-50"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
